import { useCallback, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isValidCnpj } from "./contactChannels";

export interface CompanyInfo {
  cnpj: string;
  legalName: string;
  tradeName: string;
  city: string;
  state: string;
}

export type ValidationStatus = "invalid" | "not_found" | "inactive" | "active" | "unavailable";

interface ValidationResponse {
  status: ValidationStatus;
  company?: CompanyInfo;
}

export type ValidationOutcome =
  | { kind: "active"; company: CompanyInfo }
  | { kind: "inactive" }
  | { kind: "not_found" }
  | { kind: "invalid" }
  | { kind: "unavailable" };

export function useCnpjValidation() {
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const validate = useCallback(async (rawCnpj: string): Promise<ValidationOutcome> => {
    const digits = rawCnpj.replace(/\D/g, "");
    if (!isValidCnpj(digits)) return { kind: "invalid" };

    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke<ValidationResponse>("validate-cnpj", {
        body: { cnpj: digits },
      });
      if (requestId !== requestIdRef.current) return { kind: "unavailable" };
      if (error || !data) return { kind: "unavailable" };

      switch (data.status) {
        case "active":
          return data.company
            ? { kind: "active", company: { ...data.company, cnpj: digits } }
            : { kind: "unavailable" };
        case "inactive":
          return { kind: "inactive" };
        case "not_found":
          return { kind: "not_found" };
        case "invalid":
          return { kind: "invalid" };
        default:
          return { kind: "unavailable" };
      }
    } catch {
      return { kind: "unavailable" };
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    setLoading(false);
  }, []);

  return { validate, loading, reset };
}
